import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import {
    DOCUMENT_DISPLAY_FIELDS,
    HistoryField,
} from "@common/constants/history-source-types";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import {
    formatAuditLogRefValue,
    getHistoryFieldLabel,
    isRecord,
} from "../../utils/historyDetailsHelpers";

export interface HistoryDocumentDetailsBaseRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: HistoryDetailsItem["resourceType"];
    resourceId: string;
    fileName: string;
    financialAccountId: number | null;
    financialAccountName: string | null;
}

export interface HistoryDocumentCreateDeleteDetailsRow extends HistoryDocumentDetailsBaseRow {
    previous: string;
    modified: string;
}

export interface HistoryDocumentUpdateDetailsRow extends HistoryDocumentDetailsBaseRow {
    changedField: string | null;
    changedFieldLabel: string | null;
    previous: string;
    modified: string;
    previousAccountId: number | null;
    modifiedAccountId: number | null;
    previousEntityId: number | null;
    modifiedEntityId: number | null;
    previousCategoryId: string | null;
    modifiedCategoryId: string | null;
}

const DOCUMENT_DISPLAY_FIELDS_SET = new Set<string>(DOCUMENT_DISPLAY_FIELDS);

function filterDocumentDisplayableDiffs(diffs: AuditLogDiff[]): AuditLogDiff[] {
    return diffs.filter((diff) => DOCUMENT_DISPLAY_FIELDS_SET.has(diff.field));
}

function readSnapshot(
    log: HistoryDetailsItem,
    key: "beforeSnapshot" | "afterSnapshot",
): Record<string, unknown> | undefined {
    if (!isRecord(log.metadata)) {
        return undefined;
    }
    const snap = log.metadata[key];
    return isRecord(snap) ? snap : undefined;
}

function preferredSnapshot(
    log: HistoryDetailsItem,
): Record<string, unknown> | undefined {
    const before = readSnapshot(log, "beforeSnapshot");
    const after = readSnapshot(log, "afterSnapshot");
    return log.changeType === AuditLogChangeType.DELETE
        ? (before ?? after)
        : (after ?? before);
}

function readString(
    snapshot: Record<string, unknown> | undefined,
    key: string,
): string | null {
    if (!snapshot) {
        return null;
    }
    const v = snapshot[key];
    return typeof v === "string" ? v : null;
}

function readNumber(
    snapshot: Record<string, unknown> | undefined,
    key: string,
): number | null {
    if (!snapshot) {
        return null;
    }
    const v = snapshot[key];
    return typeof v === "number" ? v : null;
}

function readReferenceNumber(
    log: HistoryDetailsItem,
    field: HistoryField,
): number | null {
    const diff = log.diff.find((item) => item.field === field);
    if (!diff) {
        return null;
    }
    const ref =
        log.changeType === AuditLogChangeType.DELETE ? diff.before : diff.after;
    return readDiffNumber(ref);
}

function resolveDocumentName(log: HistoryDetailsItem): string {
    const resourceLabel = log.resource?.label;
    if (typeof resourceLabel === "string" && resourceLabel.length > 0) {
        return resourceLabel;
    }
    const snapshot = preferredSnapshot(log);
    const fileName = readString(snapshot, "fileName");
    if (fileName) {
        return fileName;
    }
    const fileNameDiff = log.diff.find(
        (diff) => diff.field === HistoryField.FILE_NAME,
    );
    if (fileNameDiff) {
        const ref =
            log.changeType === AuditLogChangeType.DELETE
                ? fileNameDiff.before
                : fileNameDiff.after;
        const v = formatAuditLogRefValue(ref);
        if (v !== "-") {
            return v;
        }
    }
    return "Document";
}

function buildBase(log: HistoryDetailsItem): HistoryDocumentDetailsBaseRow {
    const snapshot = preferredSnapshot(log);
    const financialAccountId =
        readNumber(snapshot, "financialAccountId") ??
        readReferenceNumber(log, HistoryField.FINANCIAL_ACCOUNT_ID);
    const entityId =
        log.entityId ??
        readNumber(snapshot, "entityId") ??
        readReferenceNumber(log, HistoryField.ENTITY_ID);

    return {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        fileName: resolveDocumentName(log),
        financialAccountId,
        financialAccountName: readString(snapshot, "financialAccountName"),
        logDate: log.createdAt,
        date: log.createdAt,
        entityId,
    };
}

export function buildDocumentCreateDeleteDetailsRow(
    log: HistoryDetailsItem,
): HistoryDocumentCreateDeleteDetailsRow {
    const base = buildBase(log);
    const isDelete = log.changeType === AuditLogChangeType.DELETE;
    return {
        ...base,
        previous: isDelete ? base.fileName : "-",
        modified: isDelete ? "-" : base.fileName,
    };
}

function readDiffNumber(
    ref: AuditLogDiff["before"] | AuditLogDiff["after"],
): number | null {
    const v = ref.value ?? null;
    if (typeof v === "number") {
        return v;
    }
    if (typeof v === "string" && v.length > 0 && !Number.isNaN(Number(v))) {
        return Number(v);
    }
    return null;
}

function readDiffString(
    ref: AuditLogDiff["before"] | AuditLogDiff["after"],
): string | null {
    const v = ref.value ?? null;
    return typeof v === "string" && v.length > 0 ? v : null;
}

function buildUpdateRowForDiff(
    log: HistoryDetailsItem,
    base: HistoryDocumentDetailsBaseRow,
    diff: AuditLogDiff,
): HistoryDocumentUpdateDetailsRow {
    const isAccount = diff.field === HistoryField.FINANCIAL_ACCOUNT_ID;
    const isEntity = diff.field === HistoryField.ENTITY_ID;
    const isCategory = diff.field === HistoryField.DOCUMENT_CATEGORY_ID;
    return {
        ...base,
        id: `${log.id}-${diff.field}`,
        changedField: diff.field,
        changedFieldLabel: getHistoryFieldLabel(diff.field),
        previous: formatAuditLogRefValue(diff.before),
        modified: formatAuditLogRefValue(diff.after),
        previousAccountId: isAccount ? readDiffNumber(diff.before) : null,
        modifiedAccountId: isAccount ? readDiffNumber(diff.after) : null,
        previousEntityId: isEntity ? readDiffNumber(diff.before) : null,
        modifiedEntityId: isEntity ? readDiffNumber(diff.after) : null,
        previousCategoryId: isCategory ? readDiffString(diff.before) : null,
        modifiedCategoryId: isCategory ? readDiffString(diff.after) : null,
    };
}

export function buildDocumentUpdateDetailsRows(
    log: HistoryDetailsItem,
    selectedFields?: string[],
): HistoryDocumentUpdateDetailsRow[] {
    const base = buildBase(log);

    if (
        log.changeType === AuditLogChangeType.CREATE ||
        log.changeType === AuditLogChangeType.DELETE
    ) {
        const cd = buildDocumentCreateDeleteDetailsRow(log);
        return [
            {
                ...base,
                changedField: null,
                changedFieldLabel: null,
                previous: cd.previous,
                modified: cd.modified,
                previousAccountId: null,
                modifiedAccountId: null,
                previousEntityId: null,
                modifiedEntityId: null,
                previousCategoryId: null,
                modifiedCategoryId: null,
            },
        ];
    }

    const displayableDiffs = filterDocumentDisplayableDiffs(log.diff);
    const filteredDiffs =
        selectedFields && selectedFields.length > 0
            ? displayableDiffs.filter((diff) =>
                  selectedFields.includes(diff.field),
              )
            : displayableDiffs;
    if (filteredDiffs.length === 0) {
        return [];
    }

    return filteredDiffs.map((diff) => buildUpdateRowForDiff(log, base, diff));
}
